import moment from "moment"
import { DATE_FORMAT } from "helpers/dates"
import { getDate } from "modules/intraday/selectors/dates"

export function isDayBlocked(date, isIntradayEnabled, isIntradayBatchAvailable, unmodeledDates = [], latestDate = null, earliestDate = null) {
  if (date.isoWeekday() > 5) {
    return true
  }

  const isToday = date.isSame(getDate(), "day")
  if (isIntradayEnabled && isToday) {
    return false
  }

  const dateStr = date.format(DATE_FORMAT)
  if (unmodeledDates.includes(dateStr)) {
    return true
  }

  const isBeforeEarliestDate = earliestDate && dateStr < earliestDate
  const isAfterLatestDate = latestDate && dateStr > latestDate
  return isBeforeEarliestDate || isAfterLatestDate
}

export function isMonthBlocked(date, isIntradayEnabled, isIntradayBatchAvailable, latestDate = null, earliestDate = null) {
  if (isIntradayEnabled) {
    const currentDate = moment(getDate())
    const isCurrentMonth = currentDate.year() === date.year() && currentDate.month() === date.month()
    if (isCurrentMonth) {
      return false
    }
  }

  const firstDayOfMonth = date.clone().startOf("month").format(DATE_FORMAT)
  const lastDayOfMonth = date.clone().endOf("month").format(DATE_FORMAT)

  const isBeforeEarliestDate = earliestDate && lastDayOfMonth < earliestDate
  const isAfterLatestDate = latestDate && firstDayOfMonth > latestDate
  return isBeforeEarliestDate || isAfterLatestDate
}

export function isYearBlocked(date, isIntradayEnabled, isIntradayBatchAvailable, latestDate = null, earliestDate = null) {
  const year = date.year()

  if (isIntradayEnabled) {
    const currentYear = moment(getDate()).year()
    if (year === currentYear) {
      return false
    }
  }

  const earliestYear = earliestDate ? moment(earliestDate).year() : null
  const latestYear = latestDate ? moment(latestDate).year() : null
  const isBeforeEarliestYear = earliestYear && year < earliestYear
  const isAfterLatestYear = latestYear && year > latestYear

  return isBeforeEarliestYear || isAfterLatestYear
}

/**
 * Computes adjusted start and end dates based on data availability constraints.
 *
 * This function ensures that the provided date range falls within available data bounds
 * and handles various edge cases including weekends, unmodeled dates, and intraday scenarios.
 *
 * @param {moment.Moment} startDate - The initial start date to potentially adjust
 * @param {moment.Moment} endDate - The initial end date to potentially adjust
 * @param {moment.Moment} earliestDate - The earliest date with available data
 * @param {moment.Moment} latestDate - The latest date with available data
 * @param {string[]} [unmodeledDates=[]] - Array of date strings (YYYY-MM-DD) that should be blocked
 * @param {boolean} [isIntradayEnabled=false] - Whether intraday mode is enabled
 * @param {boolean} [isIntradayBatchAvailable=false] - Whether intraday data is currently available
 * @param {boolean} [isEndDateToday=false] - Whether the end date is today
 *
 * @returns {Object} Object containing adjusted dates
 * @returns {string} [returns.startDate] - Adjusted start date in YYYY-MM-DD format (only present if adjustment needed)
 * @returns {string} [returns.endDate] - Adjusted end date in YYYY-MM-DD format (only present if adjustment needed)

 */
export function computeAdjustedDates(
  startDate,
  endDate,
  earliestDate,
  latestDate,
  unmodeledDates = [],
  isIntradayEnabled = false,
  isIntradayBatchAvailable = false,
  isEndDateToday = false,
  skipAutoAdjustment = false,
) {
  const adjusted = {}

  if (skipAutoAdjustment) {
    return adjusted
  }

  const isValidIntradayEndDate = isIntradayEnabled && isEndDateToday

  if (!isValidIntradayEndDate && (startDate.isBefore(earliestDate) || startDate.isAfter(latestDate))) {
    adjusted.startDate = earliestDate
  } else if (isDayBlocked(startDate, isIntradayEnabled, isIntradayBatchAvailable, unmodeledDates, latestDate, earliestDate)) {
    adjusted.startDate = startDate.clone().add(1, "weeks").isoWeekday(1).format(DATE_FORMAT)
  }

  if (!isValidIntradayEndDate && (endDate.isAfter(latestDate) || endDate.isBefore(earliestDate))) {
    adjusted.endDate = latestDate
  } else if (isDayBlocked(endDate, isIntradayEnabled, isIntradayBatchAvailable, unmodeledDates, latestDate, earliestDate)) {
    adjusted.endDate = endDate.clone().isoWeekday(5).format(DATE_FORMAT)
  }

  return adjusted
}
